import React, { useEffect, useState } from 'react';
import { Scale, Loader2, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useAdminStore } from '../../store/adminStore';

const STATUS_STYLES: Record<string, { label: string; cls: string }> = {
  pending: { label: 'En attente', cls: 'bg-warning/10 text-warning' },
  accepted: { label: 'Acceptée', cls: 'bg-success/10 text-success' },
  rejected: { label: 'Rejetée', cls: 'bg-error/10 text-error' },
};

const AdminAppeals: React.FC = () => {
  const { appeals, isLoadingAppeals, fetchAppeals, reviewAppeal } = useAdminStore();
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [response, setResponse] = useState('');

  useEffect(() => { fetchAppeals(filter); }, [filter]);

  const handleReview = async (id: string, status: 'accepted' | 'rejected') => {
    const ok = await reviewAppeal(id, status, response || undefined);
    if (ok) {
      setExpandedId(null);
      setResponse('');
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-text-primary">Contestations</h2>
            <p className="text-xs text-text-muted mt-0.5">Appels soumis par les utilisateurs sanctionnés</p>
          </div>
          <div className="flex gap-1 bg-bg-tertiary/50 rounded-lg p-0.5">
            {['pending', 'accepted', 'rejected'].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  filter === s ? 'bg-bg-secondary text-text-primary shadow-sm' : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                {STATUS_STYLES[s].label}
              </button>
            ))}
          </div>
        </div>

        {isLoadingAppeals ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-admin-accent" />
          </div>
        ) : appeals.length === 0 ? (
          <div className="text-center py-16">
            <Scale className="h-10 w-10 text-text-muted/20 mx-auto mb-3" />
            <p className="text-sm text-text-muted">Aucune contestation {filter === 'pending' ? 'en attente' : ''}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {appeals.map((a) => {
              const isExpanded = expandedId === a.id;
              const s = STATUS_STYLES[a.status] || STATUS_STYLES.pending;
              return (
                <div key={a.id} className="bg-bg-secondary/60 rounded-xl border border-border-color/50 overflow-hidden">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : a.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-highlight/20 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-text-primary">@{a.username}</span>
                        <span className="text-[10px] bg-bg-tertiary text-text-secondary px-1.5 py-0.5 rounded">{a.action_type}</span>
                      </div>
                      <p className="text-xs text-text-muted mt-0.5 truncate">{a.reason}</p>
                    </div>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-text-muted" /> : <ChevronDown className="h-4 w-4 text-text-muted" />}
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-border-color/30 pt-3">
                      <div className="bg-bg-tertiary/50 rounded-lg p-3 space-y-1">
                        <p className="text-xs text-text-muted">Action originale:</p>
                        <p className="text-sm text-text-secondary">{a.action_reason}</p>
                      </div>
                      <div className="bg-bg-tertiary/50 rounded-lg p-3 space-y-1">
                        <p className="text-xs text-text-muted">Raison de la contestation:</p>
                        <p className="text-sm text-text-primary">{a.reason}</p>
                      </div>

                      {a.status === 'pending' && (
                        <div className="space-y-2">
                          <textarea
                            value={response}
                            onChange={(e) => setResponse(e.target.value)}
                            placeholder="Réponse admin (optionnel)..."
                            rows={2}
                            className="w-full px-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted resize-none"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleReview(a.id, 'accepted')}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-success/10 text-success text-xs font-medium hover:bg-success/20"
                            >
                              <Check className="h-3 w-3" /> Accepter (révoquer sanction)
                            </button>
                            <button
                              onClick={() => handleReview(a.id, 'rejected')}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-error/10 text-error text-xs font-medium hover:bg-error/20"
                            >
                              <X className="h-3 w-3" /> Rejeter
                            </button>
                          </div>
                        </div>
                      )}

                      {a.admin_response && (
                        <div className="bg-admin-accent/5 border border-admin-accent/20 rounded-lg p-3">
                          <p className="text-xs text-admin-accent/60 mb-1">Réponse admin:</p>
                          <p className="text-sm text-text-secondary">{a.admin_response}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAppeals;
